import { useAuth } from '../context/AuthContext';
import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Zap, MapPin, Trophy, ChevronRight, ArrowLeft } from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const VIDEOS = [
    '/videos/electric_dirtbike.mp4',
    '/videos/skateboard.mp4',
    '/videos/electric_longboard.mp4',
    '/videos/euc.mp4',
    '/videos/gas_dirtbike.mp4',
    '/videos/surfer.mp4',
];

export default function LandingPage() {
    const { login, isAuthenticated } = useAuth();
    const navigate = useNavigate();
    const [splashDone, setSplashDone] = useState(false);
    const [videoReady, setVideoReady] = useState(false);
    const [isInAppBrowser, setIsInAppBrowser] = useState(false);
    const [showAuthForm, setShowAuthForm] = useState(null);
    const videoRef = useRef(null);
    const splashRef = useRef(null);
    const [currentVideoIndex] = useState(() => Math.floor(Math.random() * VIDEOS.length));

    useEffect(() => {
        const ua = navigator.userAgent || navigator.vendor || '';
        const inApp = /FBAN|FBAV|Instagram|Snapchat|Twitter|TikTok|Line|KAKAOTALK|Pinterest|LinkedInApp|MicroMessenger|Weibo/i.test(ua);
        // Also check for generic webview
        const webview = /wv|WebView/i.test(ua) && /Android/i.test(ua);
        setIsInAppBrowser(inApp || webview);
    }, []);

    const dismissSplash = () => {
        setSplashDone(true);
        if (splashRef.current) {
            splashRef.current.style.opacity = '0';
            splashRef.current.style.pointerEvents = 'none';
        }
    };

    const handleEmailAuth = (userData) => {
        window.location.href = '/dashboard';
    };

    const handleWalletAuth = (userData) => {
        window.location.href = '/dashboard';
    };

    useEffect(() => {
        const timer = setTimeout(() => dismissSplash(), 6000);
        return () => clearTimeout(timer);
    }, []);

    const handleVideoLoaded = () => {
        setVideoReady(true);
        if (videoRef.current) {
            videoRef.current.playbackRate = 0.6;
            // Speed up after 3 seconds
            setTimeout(() => {
                if (videoRef.current) videoRef.current.playbackRate = 1.0;
            }, 3000);
        }
    };

    const features = [
        { id: 'earn', icon: Zap, title: 'Earn Boltz', desc: 'Get rewarded with Boltz coins for every kilometer you ride.', color: 'text-boltz-gold', border: 'hover:border-boltz-gold/30', glow: 'shadow-[0_0_15px_rgba(255,180,0,0.1)]' },
        { id: 'airdrops', icon: MapPin, title: 'Find Airdrops', desc: 'Discover hidden Boltz drops across your city.', color: 'text-plasma-blue', border: 'hover:border-plasma-blue/30', glow: 'shadow-[0_0_15px_rgba(30,144,255,0.1)]' },
        { id: 'compete', icon: Trophy, title: 'Compete', desc: 'Climb the leaderboards and unlock exclusive gear.', color: 'text-plasma-red', border: 'hover:border-plasma-red/30', glow: 'shadow-[0_0_15px_rgba(255,32,32,0.1)]' },
    ];

    return (
        <div className="min-h-screen relative overflow-hidden bg-background text-foreground">
            {/* Cinematic Splash Intro */}
            <div
                ref={splashRef}
                className="fixed inset-0 z-[100] flex items-center justify-center bg-black"
                style={{ transition: 'opacity 1s ease-in-out' }}
                data-testid="splash-screen"
            >
                {/* Video Background */}
                <video
                    ref={videoRef}
                    src={VIDEOS[currentVideoIndex]}
                    className="absolute inset-0 w-full h-full object-cover"
                    autoPlay
                    muted
                    playsInline
                    onCanPlay={handleVideoLoaded}
                    onEnded={dismissSplash}
                    style={{ opacity: videoReady ? 1 : 0, transition: 'opacity 0.5s' }}
                />

                {/* Dark gradient overlay */}
                <div className="absolute inset-0 bg-gradient-to-t from-black via-black/60 to-black/30 z-10 pointer-events-none" />
                <div className="absolute inset-0 bg-gradient-to-b from-black/40 via-transparent to-black z-10 pointer-events-none" />

                {/* Logo + Text Overlay */}
                <div className="relative z-20 text-center px-6">
                    <motion.img
                        src="/boltorium_graffiti.png"
                        alt="Boltorium"
                        className="w-80 sm:w-[28rem] md:w-[36rem] mx-auto object-contain drop-shadow-[0_0_30px_rgba(255,255,255,0.3)]"
                        initial={{ opacity: 0, scale: 0.8 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ delay: 0.3, duration: 1 }}
                        data-testid="splash-logo"
                    />
                    <motion.p
                        className="text-base sm:text-lg text-white/50 mt-2 tracking-[0.4em] font-display"
                        initial={{ opacity: 0 }}
                        animate={{ opacity: 1 }}
                        transition={{ delay: 1.5, duration: 0.8 }}
                    >
                        RIDE THE LIGHTNING
                    </motion.p>
                </div>

                {/* Skip button */}
                <button
                    className="absolute bottom-10 right-10 z-20 pointer-events-auto text-white/40 hover:text-white/80 text-sm tracking-widest uppercase transition-colors cursor-pointer"
                    style={{ fontFamily: 'var(--font-display)' }}
                    onClick={dismissSplash}
                    data-testid="skip-splash-btn"
                >
                    Skip
                </button>

                {/* Bottom neon line */}
                <motion.div
                    className="absolute bottom-0 left-0 right-0 h-[2px] bg-gradient-to-r from-transparent via-primary to-transparent z-20"
                    initial={{ scaleX: 0 }}
                    animate={{ scaleX: 1 }}
                    transition={{ delay: 0.5, duration: 2, ease: 'easeOut' }}
                />
            </div>

            {/* Main Landing Content */}
            <div className="min-h-screen flex flex-col items-center justify-center relative">
                {/* Background Effects */}
                <div className="absolute inset-0 bg-[radial-gradient(circle_at_center,_var(--tw-gradient-stops))] from-primary/10 via-background to-background z-0" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_left,rgba(0,255,138,0.08)_0%,transparent_50%)] z-0" />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(122,0,255,0.08)_0%,transparent_50%)] z-0" />

                {/* Hero Content */}
                <div className="relative z-10 max-w-4xl px-6 text-center">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ duration: 0.8 }}
                    >
                        <div className="mb-8 flex justify-center">
                            <img
                                src="/boltorium_graffiti.png"
                                alt="Boltorium"
                                className="w-72 sm:w-96 md:w-[30rem] object-contain drop-shadow-[0_0_25px_rgba(255,255,255,0.2)]"
                                data-testid="hero-logo"
                            />
                        </div>

                        <h1 className="text-4xl sm:text-6xl lg:text-8xl font-black mb-6 tracking-tight font-display gradient-text drop-shadow-sm">
                            RIDE THE LIGHTNING
                        </h1>

                        <p className="text-lg sm:text-xl md:text-2xl text-muted-foreground mb-12 max-w-2xl mx-auto font-light leading-relaxed" style={{ fontFamily: 'var(--font-body)' }}>
                            The future of electrified riding is here. Track rides, collect airdrops, and earn{' '}
                            <span className="text-boltz-gold font-bold text-glow-gold">Boltz</span> coins on Solana <span className="text-muted-foreground">(devnet demo &mdash; token minting is not live)</span>.
                        </p>

                        {isInAppBrowser && (
                            <div className="mb-8 mx-auto max-w-md p-4 rounded-xl bg-neon-gold/10 border border-neon-gold/20 text-center" data-testid="inapp-browser-warning">
                                <p className="text-neon-gold text-sm font-bold font-display tracking-wider mb-2">OPEN IN BROWSER</p>
                                <p className="text-white/70 text-xs" style={{ fontFamily: 'var(--font-body)' }}>
                                    Google login requires a full browser. Tap the menu and select &quot;Open in Chrome&quot; or &quot;Open in Safari&quot;.
                                </p>
                            </div>
                        )}

                        {/* Multi-auth login options */}
                        {!showAuthForm ? (
                            <div className="flex flex-col gap-3 max-w-sm mx-auto" data-testid="auth-options">
                                <Button onClick={login}
                                    className="w-full py-5 rounded-full bg-white text-black font-bold text-sm hover:bg-white/90 flex items-center justify-center gap-3"
                                    style={{ fontFamily: 'var(--font-display)' }}
                                    data-testid="google-login-btn">
                                    <svg className="w-5 h-5" viewBox="0 0 24 24"><path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/><path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/><path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/><path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/></svg>
                                    Continue with Google
                                </Button>

                                <Button onClick={() => setShowAuthForm('email')}
                                    className="w-full py-5 rounded-full bg-primary/10 text-primary border border-primary/30 font-bold text-sm hover:bg-primary/20 flex items-center justify-center gap-3"
                                    style={{ fontFamily: 'var(--font-display)' }}
                                    data-testid="email-login-btn">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z"/><path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z"/></svg>
                                    Continue with Email
                                </Button>

                                <Button onClick={() => setShowAuthForm('wallet')}
                                    className="w-full py-5 rounded-full bg-cyber-purple/10 text-cyber-purple border border-cyber-purple/30 font-bold text-sm hover:bg-cyber-purple/20 flex items-center justify-center gap-3"
                                    style={{ fontFamily: 'var(--font-display)' }}
                                    data-testid="wallet-login-btn">
                                    <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20"><path fillRule="evenodd" d="M17.707 9.293a1 1 0 010 1.414l-7 7a1 1 0 01-1.414 0l-7-7A.997.997 0 012 10V5a3 3 0 013-3h5c.256 0 .512.098.707.293l7 7zM5 6a1 1 0 100-2 1 1 0 000 2z" clipRule="evenodd"/></svg>
                                    Connect Solana Wallet
                                </Button>
                            </div>
                        ) : showAuthForm === 'email' ? (
                            <EmailAuthForm onBack={() => setShowAuthForm(null)} onSuccess={handleEmailAuth} />
                        ) : showAuthForm === 'wallet' ? (
                            <WalletAuthForm onBack={() => setShowAuthForm(null)} onSuccess={handleWalletAuth} />
                        ) : null}
                    </motion.div>

                    {/* Stats */}
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.3, duration: 0.8 }}
                        className="mt-20 grid grid-cols-3 gap-2 sm:gap-8"
                    >
                        {[
                            { val: 'GPS', label: 'VERIFIED RIDES' },
                            { val: 'BOLTZ', label: 'EARN AS YOU RIDE' },
                            { val: 'DEVNET', label: 'SOLANA DEMO' },
                        ].map((stat) => (
                            <div key={stat.label} className="text-center min-w-0">
                                <p className="text-xl sm:text-4xl lg:text-5xl font-black text-foreground font-display tracking-tight text-glow">
                                    {stat.val}
                                </p>
                                <p className="label-text mt-2 text-primary min-w-0 text-[9px] sm:text-xs leading-tight break-words whitespace-normal">{stat.label}</p>
                            </div>
                        ))}
                    </motion.div>

                    {/* Feature Grid */}
                    <motion.div
                        className="grid grid-cols-1 md:grid-cols-3 gap-6 mt-24 text-left pb-16"
                        initial={{ opacity: 0, y: 40 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: 0.5, duration: 0.8 }}
                    >
                        {features.map((feature) => (
                            <div
                                key={feature.id}
                                className={`p-6 rounded-2xl bg-white/5 border border-white/5 backdrop-blur-md ${feature.border} ${feature.glow} transition-all duration-300`}
                            >
                                <feature.icon className={`w-8 h-8 ${feature.color} mb-4`} />
                                <h3 className="text-lg font-bold mb-2 font-display text-foreground">{feature.title}</h3>
                                <p className="text-muted-foreground text-sm" style={{ fontFamily: 'var(--font-body)' }}>{feature.desc}</p>
                            </div>
                        ))}
                    </motion.div>
                </div>

                    {/* Legal footer links */}
                    <div className="relative z-20 mt-16 pb-8 text-center text-[11px] text-white/40 space-x-4">
                        <a href="/privacy" className="hover:text-white/70 underline underline-offset-2">Privacy</a>
                        <a href="/terms" className="hover:text-white/70 underline underline-offset-2">Terms</a>
                    </div>

                {/* Footer decorative lines */}
                <div className="absolute bottom-0 w-full h-1 bg-gradient-to-r from-transparent via-neon-blue to-transparent opacity-50" />
                <div className="absolute bottom-0 w-full h-[2px] bg-gradient-to-r from-transparent via-hot-pink to-transparent opacity-30 translate-y-[-2px]" />
            </div>
        </div>
    );
}

/* ─── Email Auth Form ─── */
function EmailAuthForm({ onBack, onSuccess }) {
    const [isLogin, setIsLogin] = useState(true);
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [name, setName] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);
    const [forgot, setForgot] = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError('');
        setLoading(true);
        try {
            const endpoint = isLogin ? '/auth/login' : '/auth/register';
            const body = isLogin ? { email, password } : { email, password, name };
            const res = await fetch(`${API}${endpoint}`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include', body: JSON.stringify(body),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.detail || 'Auth failed'); return; }
            onSuccess(data);
        } catch { setError('Network error'); }
        finally { setLoading(false); }
    };

    if (forgot) return <ForgotPasswordForm onBack={() => setForgot(false)} />;

    return (
        <motion.form initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            onSubmit={handleSubmit} className="max-w-sm mx-auto space-y-3" data-testid="email-auth-form">
            <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white mb-2">
                <ArrowLeft className="w-3 h-3" /> Back to login options
            </button>
            <h3 className="font-display text-lg text-primary">{isLogin ? 'Sign In' : 'Create Account'}</h3>
            {!isLogin && (
                <Input value={name} onChange={e => setName(e.target.value)} placeholder="Your name" className="bg-white/5 border-white/10" data-testid="auth-name-input" />
            )}
            <Input type="email" value={email} onChange={e => setEmail(e.target.value)} placeholder="Email address" required className="bg-white/5 border-white/10" data-testid="auth-email-input" />
            <Input type="password" value={password} onChange={e => setPassword(e.target.value)} placeholder="Password (6+ chars)" required minLength={6} className="bg-white/5 border-white/10" data-testid="auth-password-input" />
            {error && <p className="text-plasma-red text-xs" data-testid="auth-error">{error}</p>}
            <Button type="submit" disabled={loading} className="w-full py-5 rounded-full bg-primary text-black font-display" data-testid="auth-submit-btn">
                {loading ? '...' : isLogin ? 'Sign In' : 'Create Account'}
            </Button>
            {isLogin && (
                <button type="button" onClick={() => setForgot(true)}
                    className="text-xs text-muted-foreground hover:text-primary block mx-auto" data-testid="forgot-password-link">
                    Forgot password?
                </button>
            )}
            <p className="text-center text-xs text-muted-foreground">
                {isLogin ? "Don't have an account? " : "Already have an account? "}
                <button type="button" onClick={() => { setIsLogin(!isLogin); setError(''); }} className="text-primary hover:underline" data-testid="auth-toggle">
                    {isLogin ? 'Sign up' : 'Sign in'}
                </button>
            </p>
        </motion.form>
    );
}

/* ─── Wallet Auth Form ─── */
function WalletAuthForm({ onBack, onSuccess }) {
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const connectPhantom = async () => {
        setError('');
        setLoading(true);
        try {
            if (!window.solana?.isPhantom) {
                window.open('https://phantom.app/', '_blank');
                setError('Phantom wallet not found. Install it and try again.');
                setLoading(false);
                return;
            }
            const resp = await window.solana.connect();
            const walletAddress = resp.publicKey.toString();
            const message = `Sign in to Boltorium: ${Date.now()}`;
            const encodedMsg = new TextEncoder().encode(message);
            const signedMsg = await window.solana.signMessage(encodedMsg, 'utf8');

            const res = await fetch(`${API}/auth/wallet`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ wallet_address: walletAddress, message, signature: Array.from(signedMsg.signature) }),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.detail || 'Wallet auth failed'); return; }
            onSuccess(data);
        } catch (e) { setError(e.message || 'Wallet connection failed'); }
        finally { setLoading(false); }
    };

    const connectManual = async () => {
        setError('');
        const addr = prompt('Enter your Solana wallet address:');
        if (!addr || addr.length < 30) { setError('Invalid address'); return; }
        setLoading(true);
        try {
            const res = await fetch(`${API}/auth/wallet`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                credentials: 'include',
                body: JSON.stringify({ wallet_address: addr }),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.detail || 'Failed'); return; }
            onSuccess(data);
        } catch { setError('Network error'); }
        finally { setLoading(false); }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-sm mx-auto space-y-3" data-testid="wallet-auth-form">
            <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white mb-2">
                <ArrowLeft className="w-3 h-3" /> Back to login options
            </button>
            <h3 className="font-display text-lg text-cyber-purple">Connect Wallet</h3>
            <Button onClick={connectPhantom} disabled={loading}
                className="w-full py-5 rounded-full bg-[#AB9FF2] text-white font-display text-sm hover:bg-[#AB9FF2]/80" data-testid="phantom-connect-btn">
                <svg className="w-5 h-5 mr-2" viewBox="0 0 128 128" fill="white"><path d="M110.28 64.09c0-22.66-18.86-41.09-42.14-41.09S25.99 41.43 25.99 64.09c0 16.06 9.33 29.93 22.88 36.58a3.78 3.78 0 003.47-.12 3.87 3.87 0 001.68-3.14V88.8a3.88 3.88 0 00-2.4-3.58c-9.16-3.78-15.63-12.74-15.63-23.13 0-13.94 11.64-25.24 26-25.24s26 11.3 26 25.24c0 10.39-6.47 19.35-15.63 23.13a3.88 3.88 0 00-2.4 3.58v8.61a3.87 3.87 0 001.68 3.14 3.78 3.78 0 003.47.12c13.55-6.65 22.88-20.52 22.88-36.58z"/></svg>
                {loading ? 'Connecting...' : 'Phantom Wallet'}
            </Button>
            <Button onClick={connectManual} disabled={loading} variant="outline"
                className="w-full py-5 rounded-full border-cyber-purple/30 text-cyber-purple font-display text-sm" data-testid="manual-wallet-btn">
                Enter Wallet Address
            </Button>
            {error && <p className="text-plasma-red text-xs text-center" data-testid="wallet-error">{error}</p>}
            <p className="text-center text-[10px] text-muted-foreground">Supports Phantom, Solflare, and any Solana wallet</p>
        </motion.div>
    );
}

/* ─── Forgot Password Form (dev link flow) ─── */
function ForgotPasswordForm({ onBack }) {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [token, setToken] = useState('');
    const [stage, setStage] = useState('request'); // request | reset | done
    const [error, setError] = useState('');
    const [msg, setMsg] = useState('');
    const [loading, setLoading] = useState(false);

    const requestReset = async (e) => {
        e.preventDefault();
        setError(''); setMsg(''); setLoading(true);
        try {
            const res = await fetch(`${API}/auth/forgot-password`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ email }),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.detail || 'Request failed'); return; }
            if (data.reset_token) {
                setToken(data.reset_token);
                setStage('reset');
                setMsg('Reset link verified. Choose a new password below.');
            } else {
                setMsg(data.message || 'If an account exists, a reset link was generated.');
            }
        } catch { setError('Network error'); }
        finally { setLoading(false); }
    };

    const doReset = async (e) => {
        e.preventDefault();
        setError(''); setLoading(true);
        try {
            const res = await fetch(`${API}/auth/reset-password`, {
                method: 'POST', headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify({ token, password }),
            });
            const data = await res.json();
            if (!res.ok) { setError(data.detail || 'Reset failed'); return; }
            setStage('done'); setMsg('Password reset! You can now sign in.');
        } catch { setError('Network error'); }
        finally { setLoading(false); }
    };

    return (
        <motion.div initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
            className="max-w-sm mx-auto space-y-3" data-testid="forgot-password-form">
            <button type="button" onClick={onBack} className="flex items-center gap-1 text-xs text-muted-foreground hover:text-white mb-2">
                <ArrowLeft className="w-3 h-3" /> Back to login
            </button>
            <h3 className="font-display text-lg text-primary">Reset Password</h3>

            {stage === 'request' && (
                <form onSubmit={requestReset} className="space-y-3">
                    <Input type="email" value={email} onChange={e => setEmail(e.target.value)}
                        placeholder="Your account email" required className="bg-white/5 border-white/10" data-testid="forgot-email-input" />
                    <Button type="submit" disabled={loading} className="w-full py-5 rounded-full bg-primary text-black font-display" data-testid="forgot-submit-btn">
                        {loading ? '...' : 'Get Reset Link'}
                    </Button>
                </form>
            )}

            {stage === 'reset' && (
                <form onSubmit={doReset} className="space-y-3">
                    <Input type="password" value={password} onChange={e => setPassword(e.target.value)}
                        placeholder="New password (6+ chars)" required minLength={6} className="bg-white/5 border-white/10" data-testid="reset-password-input" />
                    <Button type="submit" disabled={loading} className="w-full py-5 rounded-full bg-neon-green text-black font-display" data-testid="reset-submit-btn">
                        {loading ? '...' : 'Set New Password'}
                    </Button>
                </form>
            )}

            {stage === 'done' && (
                <Button onClick={onBack} className="w-full py-5 rounded-full bg-primary text-black font-display" data-testid="reset-done-btn">
                    Back to Sign In
                </Button>
            )}

            {msg && <p className="text-neon-green text-xs" data-testid="forgot-msg">{msg}</p>}
            {error && <p className="text-plasma-red text-xs" data-testid="forgot-error">{error}</p>}
        </motion.div>
    );
}
